import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X } from 'lucide-react';

/** Modal dialog: focus moves in, Escape and the backdrop close it, focus returns on close. */
export function Dialog({ open, title, onClose, children, footer, wide }: {
  open: boolean; title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Callers often pass a new onClose each render; read it from a ref so a re-render (a keystroke in a field)
  // never re-runs the effect below, which would move focus back to the first control.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const el = ref.current;
    // The first field, else the first button or link: a form opens ready to type.
    const first = el?.querySelector<HTMLElement>('input:not([type=hidden]):not([type=checkbox]):not([type=file]), textarea, select')
      ?? el?.querySelector<HTMLElement>('button:not([data-close]), [href]');
    (first ?? el)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); close.current(); }
      if (e.key === 'Tab' && el) {
        const items = [...el.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), textarea, select')];
        if (!items.length) return;
        const a = items[0]!, z = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
        else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); prev?.focus?.(); };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink/45 p-0 sm:items-center sm:p-6" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`max-h-[92vh] w-full overflow-auto rounded-t-2xl border border-line bg-white p-6 shadow-pop sm:rounded-2xl sm:p-7 ${wide ? 'sm:max-w-2xl' : 'sm:max-w-lg'}`}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <h2 id={titleId} className="m-0 text-xl font-bold tracking-[-0.01em]">{title}</h2>
          <button type="button" data-close onClick={onClose} aria-label="Close" className="-m-2 flex h-11 w-11 items-center justify-center rounded-[10px] border-0 bg-transparent text-muted hover:bg-panel hover:text-ink">
            <X aria-hidden="true" size={20} />
          </button>
        </div>
        <div className="flex flex-col gap-4">{children}</div>
        {footer && <div className="-mx-6 mt-6 flex flex-wrap justify-end gap-3 border-t border-line-soft px-6 pt-5 sm:-mx-7 sm:px-7">{footer}</div>}
      </div>
    </div>
  );
}
